// Table-of-contents tree of a document: roots and lazily loaded child levels (/toc?parent=), extraction-status dots for Operator and Admin, subtree requirement badges, and the ancestors of the selected clause expanded and scrolled into view.
import { useQueries, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { TocNode } from "@/api/__generated__";
import { clausesGetTocOptions } from "@/api/__generated__/@tanstack/react-query.gen";
import { useInternalDetails } from "@/app/useInternalDetails";
import { ProblemState } from "@/components/common/ProblemState";
import { TreeView } from "@/components/common/TreeView";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { STATUS_DOT } from "@/features/library/format";
import { cn } from "@/lib/cn";
import { formatCompact, plural } from "@/lib/format";
import { EXTRACTION_STATUS_LABELS } from "@/lib/labels";

export const TREE_ROW_HEIGHT = 32;

interface ClauseTreeProps {
  docUid: string;
  selectedUid: string | null;
  revealUids: readonly string[];
  onSelect: (clauseUid: string) => void;
  className?: string;
}

interface LevelResult {
  data: TocNode[] | undefined;
  isError: boolean;
  refetch: () => Promise<unknown>;
}

interface ChildLevels {
  levels: Map<string, readonly TocNode[]>;
  failed: LevelResult[];
}

export function visibleIds(
  roots: readonly TocNode[],
  levels: ReadonlyMap<string, readonly TocNode[]>,
  expanded: ReadonlySet<string>,
): string[] {
  const ids: string[] = [];
  const visit = (nodes: readonly TocNode[]) => {
    for (const node of nodes) {
      ids.push(node.clause_uid);
      const children = expanded.has(node.clause_uid) ? levels.get(node.clause_uid) : undefined;
      if (children) {
        visit(children);
      }
    }
  };
  visit(roots);
  return ids;
}

function NodeLabel({ node, internal }: { node: TocNode; internal: boolean }) {
  const titled = node.display_title !== node.number;
  return (
    <span
      className="flex min-w-0 items-center gap-2"
      title={titled ? `${node.number} ${node.display_title}` : node.number}
    >
      {internal ? (
        <span
          aria-hidden
          className={cn("size-2 shrink-0 rounded-full", STATUS_DOT[node.extraction_status])}
        />
      ) : null}
      <span
        className={cn("shrink-0 font-mono text-xs", titled ? "text-muted-foreground" : "text-foreground")}
      >
        {node.number}
      </span>
      {titled ? (
        <>
          {" "}
          <span className="min-w-0 truncate">{node.display_title}</span>
        </>
      ) : null}
      <span className="sr-only">
        , {internal ? `${EXTRACTION_STATUS_LABELS[node.extraction_status]}, ` : ""}
        {plural(node.subtree_requirement_count, "requirement")}
      </span>
      {node.subtree_requirement_count > 0 ? (
        <span
          aria-hidden
          className="ml-auto shrink-0 rounded-pill bg-muted px-1.5 text-label/[18px] font-semibold text-ink-2 tabular-nums"
        >
          {formatCompact(node.subtree_requirement_count)}
        </span>
      ) : null}
    </span>
  );
}

export function ClauseTree({ docUid, selectedUid, revealUids, onSelect, className }: ClauseTreeProps) {
  const internal = useInternalDetails();
  const roots = useQuery(clausesGetTocOptions({ path: { doc_uid: docUid } }));
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set(revealUids));
  const expandedIds = useMemo(() => [...expanded], [expanded]);
  const combine = useCallback(
    (results: LevelResult[]): ChildLevels => {
      const levels = new Map<string, readonly TocNode[]>();
      const failed: LevelResult[] = [];
      results.forEach((result, index) => {
        const id = expandedIds[index];
        if (id === undefined) {
          return;
        }
        if (result.data) {
          levels.set(id, result.data);
        } else if (result.isError) {
          levels.set(id, []);
          failed.push(result);
        }
      });
      return { levels, failed };
    },
    [expandedIds],
  );
  const children = useQueries({
    queries: expandedIds.map((id) =>
      clausesGetTocOptions({ path: { doc_uid: docUid }, query: { parent: id } }),
    ),
    combine,
  });
  const childrenOf = useCallback((id: string) => children.levels.get(id), [children.levels]);
  const revealKey = revealUids.join("\n");

  const [revealedKey, setRevealedKey] = useState(revealKey);
  if (revealKey !== revealedKey) {
    setRevealedKey(revealKey);
    const reveal = revealKey.length === 0 ? [] : revealKey.split("\n");
    if (reveal.some((uid) => !expanded.has(uid))) {
      setExpanded(new Set([...expanded, ...reveal]));
    }
  }

  const wrapperRef = useRef<HTMLDivElement>(null);
  const revealed = useRef<string | null>(null);
  const ids = useMemo(
    () => visibleIds(roots.data ?? [], children.levels, expanded),
    [roots.data, children.levels, expanded],
  );
  useEffect(() => {
    if (selectedUid === null || revealed.current === selectedUid) {
      return;
    }
    const index = ids.indexOf(selectedUid);
    const scroller = wrapperRef.current?.querySelector('[role="tree"]')?.parentElement;
    if (index < 0 || !scroller) {
      return;
    }
    revealed.current = selectedUid;
    const top = index * TREE_ROW_HEIGHT;
    if (top < scroller.scrollTop || top + TREE_ROW_HEIGHT > scroller.scrollTop + scroller.clientHeight) {
      scroller.scrollTop = Math.max(0, top - scroller.clientHeight / 3);
    }
  }, [ids, selectedUid]);

  const onExpandedChange = useCallback((id: string, open: boolean) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (open) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  if (roots.isError) {
    return (
      <ProblemState
        problem={roots.error}
        compact
        retry={() => {
          void roots.refetch();
        }}
        className="m-3"
      />
    );
  }
  if (roots.isPending) {
    return (
      <div aria-hidden className="flex flex-col gap-2 p-3">
        {Array.from({ length: 12 }, (_, index) => (
          <Skeleton key={index} className="h-5" style={{ width: `${String(55 + ((index * 37) % 40))}%` }} />
        ))}
      </div>
    );
  }
  return (
    <div ref={wrapperRef} className={cn("flex min-h-0 flex-col", className)}>
      {children.failed.length > 0 ? (
        <div className="flex items-center justify-between gap-2 border-b px-3 py-2 text-xs text-muted-foreground">
          Some levels could not be loaded.
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              for (const failed of children.failed) {
                void failed.refetch();
              }
            }}
          >
            Try again
          </Button>
        </div>
      ) : null}
      <TreeView<TocNode>
        roots={roots.data}
        getId={getId}
        getLabel={getLabel}
        hasChildren={hasChildren}
        childrenOf={childrenOf}
        expanded={expanded}
        onExpandedChange={onExpandedChange}
        selectedId={selectedUid}
        onSelect={(node) => {
          onSelect(node.clause_uid);
        }}
        renderNode={(node) => <NodeLabel node={node} internal={internal} />}
        ariaLabel="Table of contents"
        rowHeight={TREE_ROW_HEIGHT}
        className="flex-1 px-1.5 py-1.5"
      />
    </div>
  );
}

function getId(node: TocNode): string {
  return node.clause_uid;
}

function getLabel(node: TocNode): string {
  return node.number;
}

function hasChildren(node: TocNode): boolean {
  return node.child_count > 0;
}
